import React, { useState, useMemo, ReactNode, ChangeEvent } from 'react';

// Column definition for the table
export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T, index: number) => ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
}

// Filter option for dropdowns
export interface FilterOption {
  value: string;
  label: string;
}

// Filter configuration
export interface FilterConfig {
  key: string;
  label: string;
  type: 'text' | 'select';
  placeholder?: string;
  options?: FilterOption[];
}

// Props for the DataTable component
export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  filters?: FilterConfig[];
  searchPlaceholder?: string;
  searchKeys?: (keyof T)[];
  renderActions?: (item: T) => ReactNode;
  onRowClick?: (item: T) => void;
  emptyState?: {
    icon?: ReactNode;
    title: string;
    description: string;
    action?: ReactNode;
  };
  loading?: boolean;
  itemsPerPageOptions?: number[];
  defaultItemsPerPage?: number;
  defaultSortKey?: string;
  defaultSortDirection?: 'asc' | 'desc';
  title?: string;
}

function DataTable<T extends { id: string }>({
  data,
  columns,
  filters = [],
  searchPlaceholder = 'Search...',
  searchKeys = [],
  renderActions,
  onRowClick,
  emptyState,
  loading = false,
  itemsPerPageOptions = [10, 20, 50],
  defaultItemsPerPage = 10,
  defaultSortKey = 'createdAt',
  defaultSortDirection = 'desc',
  title,
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filter and search data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search filter
    if (searchTerm && searchKeys.length > 0) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) =>
        searchKeys.some((key) => {
          const value = item[key];
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchLower);
          }
          return false;
        })
      );
    }

    // Apply dropdown filters
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value) {
        result = result.filter((item) => {
          const itemValue = (item as Record<string, unknown>)[key];
          return String(itemValue) === value;
        });
      }
    });

    // Apply sorting
    if (sortKey) {
      result.sort((a, b) => {
        const aValue = (a as Record<string, unknown>)[sortKey];
        const bValue = (b as Record<string, unknown>)[sortKey];

        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else {
          comparison = aValue < bValue ? -1 : 1;
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchTerm, searchKeys, filterValues, sortKey, sortDirection]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="bg-[#27272A] border border-[#3F3F46] rounded-2xl p-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#14B8A6]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Title Header - like ManagerDashboard */}
      {title && (
        <div className="p-6 border-b border-[#3F3F46]">
          <h2 className="text-xl font-bold text-white">{title}</h2>
        </div>
      )}

      {/* Filters Section */}
      <div className="p-4 sm:p-6 border-b border-[#3F3F46]">
        {/* Mobile: Search + Filter Button */}
        <div className="flex gap-2 md:hidden">
          {searchKeys.length > 0 && (
            <div className="flex-1">
              <div className="relative">
                <svg
                  className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#9CA3AF]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-[#0F1419] border border-[#3F3F46] rounded-xl text-white placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                />
              </div>
            </div>
          )}
          {filters.length > 0 && (
            <button
              type="button"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className={`px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                showMobileFilters || Object.values(filterValues).some(v => v)
                  ? 'bg-[#14B8A6] border-[#14B8A6] text-white'
                  : 'bg-[#0F1419] border-[#3F3F46] text-[#9CA3AF]'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>
          )}
        </div>

        {/* Mobile: Expanded Filters */}
        {showMobileFilters && filters.length > 0 && (
          <div className="mt-3 space-y-3 md:hidden">
            {filters.map((filter) => (
              <div key={filter.key}>
                {filter.type === 'select' && filter.options && (
                  <select
                    value={filterValues[filter.key] || ''}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange(filter.key, e.target.value)}
                    className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#3F3F46] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                  >
                    <option value="">{filter.label}</option>
                    {filter.options.map((option, index) => (
                      <option key={`${filter.key}-${option.value}-${index}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Desktop: Full Filter Row */}
        <div className="hidden md:flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search Input */}
          {searchKeys.length > 0 && (
            <div className="flex-1 max-w-md">
              <div className="relative">
                <svg
                  className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-[#9CA3AF]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0F1419] border border-[#3F3F46] rounded-xl text-white placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                />
              </div>
            </div>
          )}

          {/* Dropdown Filters */}
          {filters.map((filter) => (
            <div key={filter.key} className="min-w-[150px]">
              {filter.type === 'select' && filter.options && (
                <select
                  value={filterValues[filter.key] || ''}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleFilterChange(filter.key, e.target.value)}
                  className="w-full px-6 py-3 bg-[#0F1419] border border-[#3F3F46] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-sm"
                >
                  <option value="">{filter.label}</option>
                  {filter.options.map((option, index) => (
                    <option key={`${filter.key}-${option.value}-${index}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}

          {/* Results Count */}
          <div className="text-sm text-[#9CA3AF] lg:ml-auto">
            {filteredData.length} of {data.length} item{data.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Table / Card View */}
      {filteredData.length === 0 ? (
        <div className="p-8 sm:p-12 text-center">
          {emptyState?.icon && (
            <div className="text-[#14B8A6] mx-auto mb-4">{emptyState.icon}</div>
          )}
          <h3 className="text-lg sm:text-xl font-medium text-white mb-2">
            {emptyState?.title || 'No items found'}
          </h3>
          <p className="text-sm sm:text-base text-[#9CA3AF] mb-6">
            {emptyState?.description || 'Try adjusting your search or filters'}
          </p>
          {emptyState?.action}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-[#3F3F4680]">
            {paginatedData.map((item, index) => (
              <div
                key={item.id}
                className={`p-4 hover:bg-white/5 transition-colors ${onRowClick ? 'cursor-pointer active:bg-white/10' : ''}`}
                onClick={() => onRowClick?.(item)}
              >
                {/* Primary info with actions inline */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    {columns[0] && (
                      columns[0].render
                        ? columns[0].render(item, startIndex + index)
                        : String((item as Record<string, unknown>)[columns[0].key] || '')
                    )}
                  </div>
                  {renderActions && (
                    <div 
                      className="flex items-center gap-1 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderActions(item)}
                    </div>
                  )}
                </div>
                
                {/* Secondary info - remaining columns as compact info */}
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-[#9CA3AF]">
                  {columns.slice(1).map((column) => {
                    const value = column.render
                      ? column.render(item, startIndex + index)
                      : (item as Record<string, unknown>)[column.key];
                    if (!value || value === '—') return null;
                    return (
                      <span key={column.key} className="inline-flex items-center">
                        {value as React.ReactNode}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-black/20">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-6 py-4 text-left text-sm font-semibold text-[#9CA3AF] uppercase tracking-wider border-b border-[#3F3F46] ${
                        column.sortable ? 'cursor-pointer hover:text-white' : ''
                      }`}
                      style={{ width: column.width }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{column.header}</span>
                        {column.sortable && (
                          <svg
                            className={`w-4 h-4 transition-transform ${
                              sortKey === column.key
                                ? 'text-[#14B8A6]'
                                : 'text-[#9CA3AF]/50'
                            } ${
                              sortKey === column.key && sortDirection === 'desc'
                                ? 'rotate-180'
                                : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                  {renderActions && (
                    <th className="px-6 py-4 text-right text-sm font-semibold text-[#9CA3AF] uppercase tracking-wider border-b border-[#3F3F46]">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3F3F4680]">
                {paginatedData.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-white/5 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick?.(item)}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="px-6 py-5 text-sm text-[#9CA3AF]"
                      >
                        {column.render
                          ? column.render(item, startIndex + index)
                          : String((item as Record<string, unknown>)[column.key] || '')}
                      </td>
                    ))}
                    {renderActions && (
                      <td className="px-6 py-5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end space-x-1">
                          {renderActions(item)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination - only show if more than 10 records */}
          {data.length > 10 && (
          <div className="px-6 py-4 border-t border-[#3F3F46]">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Items per page */}
              <div className="flex items-center space-x-2 text-sm text-[#9CA3AF]">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-2 py-1 bg-[#0F1419] border border-[#3F3F46] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#14B8A6] text-sm"
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span>per page</span>
              </div>

              {/* Page info */}
              <div className="text-sm text-[#9CA3AF]">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of{' '}
                {filteredData.length} results
              </div>

              {/* Page navigation */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="p-2 text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="First page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7 7" />
                  </svg>
                </button>

                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="px-2 text-[#9CA3AF]">...</span>
                    ) : (
                      <button
                        onClick={() => setCurrentPage(page as number)}
                        className={`min-w-[32px] h-8 px-3 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-[#14B8A6] text-white'
                            : 'text-[#9CA3AF] hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Last page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}

export default DataTable;
